<!-- Canvas Sidebar Layout -->
<div class="sidebar-canvas-wrapper sidebar-mode" [class.collapsed]="isLeftSidebarCollapsed">
  <div 
    class="sidebar-toggle-btn" 
    (click)="toggleLeftSidebar()"
    [matTooltip]="isLeftSidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
    matTooltipPosition="right">
    <mat-icon>{{ isLeftSidebarCollapsed ? 'chevron_right' : 'chevron_left' }}</mat-icon>
  </div>
  <div class="sidebar-header-background">
    <h2 [style.font-size]="runTitleFontSize">{{ runName || 'Anomaly Detection' }}</h2>
    <button mat-icon-button class="header-action-btn"
            (click)="editRunName()"
            matTooltip="Set run name"
            aria-label="Set run name">
      <mat-icon svgIcon="pencil"></mat-icon>
    </button>
  </div>
  
  <div class="sidebar-canvas" [class.content-hidden]="false">
    <div class="project-canvas-container">
      <div class="canvas-workflow sidebar-layout">
        
        <!-- Step 1: Get Data Section -->
        <div class="collapsible-section">
          <div class="section-header-collapsible" (click)="toggleDataSection()">
            <mat-icon class="expand-icon" [svgIcon]="isDataSectionExpanded ? 'expand_more' : 'chevron_right'"></mat-icon>
            <h4>Step 1: Get Data</h4>
            <mat-icon *ngIf="dataSourceId || uploadedFileData" class="section-complete-icon" svgIcon="check_circle_green"></mat-icon>
          </div>
          <div class="section-content" *ngIf="isDataSectionExpanded">
            <div class="workflow-column get-data-column">
              <div class="data-source-card">
                <!-- Data Source Card -->
                <div *ngIf="dataSourceId" 
                     class="workflow-card canvas-card data-card completed"
                     [class.highlighted]="editorMode === 'data-source'"
                     (click)="viewDataSource()">
                  <div class="timeline-connector"></div>
                  <div class="timeline-dot"></div>
                  <div class="card-header">
                    <mat-icon class="card-icon" svgIcon="description"></mat-icon>
                    <div class="card-text-content">
                      <span class="card-title">{{ dataSourceName || 'Data Source' }}</span>
                      <p class="card-subtitle">Click to Change Data</p>
                    </div>
                  </div>
                </div>
                
                <!-- Uploaded File Card -->
                <div *ngIf="uploadedFileData && !dataSourceId" 
                     class="workflow-card canvas-card data-card completed"
                     [class.highlighted]="editorMode === 'data-source'"
                     (click)="viewDataSource()">
                  <div class="timeline-connector"></div>
                  <div class="timeline-dot"></div>
                  <div class="card-header">
                    <mat-icon class="card-icon" svgIcon="upload_file"></mat-icon>
                    <div class="card-text-content">
                      <span class="card-title">Uploaded File</span>
                      <p class="card-subtitle">Click to Change Data</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Step 2: Choose Method Section -->
        <div class="collapsible-section">
          <div class="section-header-collapsible" (click)="toggleMethodSection()">
            <mat-icon class="expand-icon" [svgIcon]="isMethodSectionExpanded ? 'expand_more' : 'chevron_right'"></mat-icon>
            <h4>Step 2: Choose Method</h4>
            <mat-icon *ngIf="selectedMethod" class="section-complete-icon" svgIcon="check_circle"></mat-icon>
          </div>
          <div class="section-content" *ngIf="isMethodSectionExpanded">
            <div class="workflow-column methods-column">
              
              <div class="methods-container">
                <!-- Selected Method Card -->
                <div *ngIf="selectedMethod" class="method-row">
                  <div class="workflow-card canvas-card method-card completed clickable"
                       [class.locked]="!isMethodSupported(selectedMethod)"
                       (click)="openMethodSelection()">
                    <div class="card-header">
                      <mat-icon class="card-icon" svgIcon="radar"></mat-icon>
                      <div class="card-text-content">
                        <span class="card-title">{{ selectedMethod.name }}</span>
                        <p class="card-subtitle">Click to Change Method</p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- Select Method Card (matching Add Goal card) - Only show if no method selected -->
                <div *ngIf="!selectedMethod" class="add-method-section">
                  <div class="workflow-card canvas-card method-card add-card highlighted"
                       (click)="openMethodSelection()">
                    <mat-icon class="card-icon" svgIcon="radar"></mat-icon>
                    <span class="card-title">Select Method</span>
                  </div>
                </div>
              </div>
              
            </div>
          </div>
        </div>
        
        <!-- Step 3: Run Detection Section -->
        <div class="collapsible-section">
          <div class="section-header-collapsible" (click)="toggleRunSection()">
            <mat-icon class="expand-icon" [svgIcon]="isRunSectionExpanded ? 'expand_more' : 'chevron_right'"></mat-icon>
            <h4>Step 3: Run Detection</h4>
          </div>
          <div class="section-content" *ngIf="isRunSectionExpanded">
            <div class="workflow-column run-column">
              <div class="run-detection-section">
                <div class="workflow-card canvas-card run-card"
                     [class.completed]="canRunDetection()"
                     [class.disabled]="!canRunDetection()"
                     [class.loading]="loading"
                     (click)="hasDetectionResults && !hasParametersChanged() ? onViewResults() : (canRunDetection() ? onRunDetection() : null)">
                  <div class="timeline-connector"></div>
                  <div class="timeline-dot"></div>
                  <div class="card-header">
                    <mat-spinner *ngIf="loading" diameter="20" class="card-spinner"></mat-spinner>
                    <mat-icon *ngIf="!loading" class="card-icon" [svgIcon]="hasDetectionResults && !hasParametersChanged() ? 'visibility' : 'play_circle'"></mat-icon>
                    <div class="card-text-content">
                      <span class="card-title">{{ loading ? 'Detecting Anomalies...' : (hasDetectionResults && !hasParametersChanged() ? 'View Results' : 'Run Anomaly Detection') }}</span>
                      <p class="card-subtitle" *ngIf="loading">Please Wait...</p>
                      <p class="card-subtitle" *ngIf="!loading && hasDetectionResults && !hasParametersChanged()">View detection results</p>
                      <p class="card-subtitle" *ngIf="!loading && (!hasDetectionResults || hasParametersChanged()) && canRunDetection()">Execute detection with selected method</p>
                      <p class="card-subtitle" *ngIf="!loading && (!hasDetectionResults || hasParametersChanged()) && !canRunDetection()">{{ selectedMethod ? 'Complete required parameters' : 'Select a method first' }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
      </div>
    </div>
  </div>
</div>

<!-- Right: Editor Panel -->
<div class="content-wrapper-parent editor-wrapper-parent" [class.visible]="isEditorVisible" [class.sidebar-collapsed]="isLeftSidebarCollapsed" *ngIf="editorMode">
  <div class="content-wrapper sidebar-editor" [class.visible]="isEditorVisible">
    
    <!-- Speech bubble tooltip for first-time users - positioned to align with Select Method button -->
    <div class="speech-bubble-tooltip-editor" *ngIf="showMethodTooltip && editorMode === 'help'">
      <div class="tooltip-content">
        <strong>Start Here!</strong>
        <p>Click "Select Method" to begin</p>
      </div>
      <div class="tooltip-arrow"></div>
    </div>
    
    <div class="editor-header">
      <button *ngIf="editorMode === 'method-config'" mat-icon-button class="back-button" (click)="onBackToMethodSelection()">
        <mat-icon svgIcon="arrow_back"></mat-icon>
      </button>
      <div class="editor-header-content">
        <h2>{{ editorTitle }}</h2>
        <p *ngIf="editorSubtitle" class="editor-subtitle">{{ editorSubtitle }}</p>
      </div>
      <div class="view-mode-toggle" *ngIf="editorMode === 'results'">
        <button
          class="view-mode-toggle-btn"
          [class.active]="layoutService.getViewMode() === 'simple'"
          (click)="layoutService.setViewMode('simple')">
          Simple
        </button>
        <button
          class="view-mode-toggle-btn"
          [class.active]="layoutService.getViewMode() === 'advanced'"
          (click)="layoutService.setViewMode('advanced')">
          Advanced
        </button>
      </div>
      <button *ngIf="editorMode === 'results' && editorData" mat-stroked-button color="primary" class="finish-button" (click)="onFinish()">Finish</button>
    </div>
    
    <div class="editor-content">
      <!-- Help Content -->
      <ng-container *ngIf="editorMode === 'help' && !loadingMethods">
        <div class="canvas-help-content">
          
          
          <div class="help-steps">
            <div class="help-step">
              <div class="step-icon">
                <mat-icon svgIcon="number-1-grey"></mat-icon>
              </div>
              <div class="step-content">
                <div class="step-label">Get Data</div>
                <div class="step-description">{{ (dataSourceId || uploadedFileData) ? 'Your data source is ready!' : 'Connect your data source by uploading a file or selecting a data source.' }}</div>
              </div>
              <mat-icon *ngIf="dataSourceId || uploadedFileData" class="step-complete-icon" svgIcon="check_circle_green"></mat-icon>
            </div>
            
            <mat-icon class="step-arrow" svgIcon="arrow_forward"></mat-icon>
            
            <div class="help-step">
              <div class="step-icon">
                <mat-icon svgIcon="number-2-grey"></mat-icon>
              </div>
              <div class="step-content">
                <div class="step-label">Choose Method</div>
                <div class="step-description">Select an anomaly detection algorithm based on your data type and complexity needs.</div>
              </div>
            </div>
            
            <mat-icon class="step-arrow" svgIcon="arrow_forward"></mat-icon>
            
            <div class="help-step">
              <div class="step-icon">
                <mat-icon svgIcon="number-3-grey"></mat-icon>
              </div>
              <div class="step-content">
                <div class="step-label">Run Detection</div>
                <div class="step-description">Execute the analysis and review detailed results including visualizations and anomaly metrics.</div>
              </div>
            </div>
          </div>
          
          
        </div>
      </ng-container>
      
      <!-- Loading State -->
      <ng-container *ngIf="loading">
        <div class="loading-container">
          <mat-spinner diameter="60"></mat-spinner>
          <p class="loading-text">{{ loadingMessage }}</p>
          <div class="loading-progress-bar-container">
            <!-- Show indeterminate during upload preparation -->
            <mat-progress-bar 
              *ngIf="uploadProgress === 0 && totalBytes > 0" 
              mode="indeterminate">
            </mat-progress-bar>
            <span *ngIf="uploadProgress === 0 && totalBytes > 0" class="loading-step-text">
              Preparing {{ getFileSizeString(totalBytes) }}...
            </span>
            <!-- Show upload progress when uploading -->
            <mat-progress-bar 
              *ngIf="uploadProgress > 0 && uploadProgress < 100" 
              mode="determinate" 
              [value]="uploadProgress">
            </mat-progress-bar>
            <span *ngIf="uploadProgress > 0 && uploadProgress < 100" class="loading-step-text">
              {{ uploadStatusText }}: {{ getFileSizeString(uploadedBytes) }} of {{ getFileSizeString(totalBytes) }}
            </span>
            <!-- Show indeterminate during processing after upload -->
            <mat-progress-bar 
              *ngIf="uploadProgress === 100 && !detectionTotalSteps" 
              mode="indeterminate">
            </mat-progress-bar>
            <span *ngIf="uploadProgress === 100 && !detectionTotalSteps" class="loading-step-text">{{ uploadStatusText }}</span>
            <!-- Show detection progress -->
            <mat-progress-bar 
              *ngIf="detectionTotalSteps && (detectionCurrentStep ?? 0) === 0" 
              mode="indeterminate">
            </mat-progress-bar>
            <span *ngIf="detectionTotalSteps && (detectionCurrentStep ?? 0) === 0" class="loading-step-text">
              {{ uploadStatusText }}
            </span>
            <mat-progress-bar 
              *ngIf="detectionTotalSteps && (detectionCurrentStep ?? 0) > 0" 
              mode="determinate" 
              [value]="((detectionCurrentStep ?? 0) / detectionTotalSteps) * 100">
            </mat-progress-bar>
            <span *ngIf="((detectionCurrentStep ?? 0) > 0 && detectionTotalSteps) || fetchingDetectionResults" class="loading-step-text">
              {{ fetchingDetectionResults ? 'Fetching Results' : ('Step ' + (detectionCurrentStep ?? 0) + ' of ' + detectionTotalSteps) }}
            </span>
          </div>
        </div>
      </ng-container>
      
      <!-- Data Source Details View -->
      <ng-container *ngIf="editorMode === 'data-source' && !loadingMethods">
        <div class="datasource-info-wrapper">
          <app-datasource-info-panel
            [dataSource]="selectedDataSource"
            [uploadedFile]="uploadedFileData"
            [columnsWithTypes]="availableColumns"
            (subtitleChange)="editorSubtitle = $event"
            (fileUpdate)="onFileUpdate($event)">
          </app-datasource-info-panel>
        </div>
      </ng-container>
      
      <!-- Method Selection Stage (matching select-goal-dialog) -->
      <ng-container *ngIf="editorMode === 'method-selection' && !loadingMethods">
        <div class="item-selection-stage">
          <!-- Stage Header with Search -->
          <div class="stage-header content-box">
            <div class="tabs flex-start gap-sm">
              
            </div>
            <div class="search-and-toggle-container flex-start gap-sm">
              <div class="view-mode-toggle">
                <button
                  class="view-mode-toggle-btn"
                  [class.active]="layoutService.getViewMode() === 'simple'"
                  (click)="layoutService.setViewMode('simple')">
                  Simple
                </button>
                <button
                  class="view-mode-toggle-btn"
                  [class.active]="layoutService.getViewMode() === 'advanced'"
                  (click)="layoutService.setViewMode('advanced')">
                  Advanced
                </button>
              </div>

              <div class="vertical-divider"></div>
              
              <div class="search-box card-white rounded-sm flex-start gap-sm">
                <mat-icon svgIcon="search"></mat-icon>
                <input type="text" 
                      placeholder="Filter by method or complexity" 
                      class="search-input"
                      [(ngModel)]="searchQuery"
                     (ngModelChange)="filterMethods()">
              </div>
            </div>
          </div>

          <!-- Methods Content Wrapper -->
          <div class="items-content-wrapper">
            <!-- Scroll Gradients -->
            <div class="scroll-gradient scroll-gradient-top" [class.visible]="hasScrollTop"></div>
            <div class="scroll-gradient scroll-gradient-bottom" [class.visible]="hasScrollBottom"></div>

            <div class="items-content" (scroll)="onMethodsContentScroll($event)">
              
              <!-- Loading State -->
              <div *ngIf="loadingMethods" class="loading-container">
                <mat-spinner diameter="40"></mat-spinner>
                <p>Loading methods...</p>
              </div>
              
              <!-- Error State -->
              <div *ngIf="error && !loadingMethods" class="error-container">
                <mat-icon svgIcon="error"></mat-icon>
                <p>{{ error }}</p>
                <button mat-button (click)="loadMethods()">Retry</button>
              </div>
              
              <!-- Methods grouped by complexity -->
              <div class="items-content-grouped" *ngIf="!loadingMethods && !error">
                <!-- Single panel with all methods -->
                <div class="content-box method-selection-panel">
                  <div class="items-grid">
                    <div *ngFor="let method of filteredMethods" 
                         class="card-selectable task-card"
                         [class.selected]="selectedMethod?.methodId === method.methodId"
                         [class.locked]="!isMethodSupported(method)"
                         (click)="selectMethodForConfig(method)">
                      <div class="item-header">
                        <mat-icon class="item-icon method-icon" svgIcon="radar"></mat-icon>
                        <div class="item-info">
                          <h5>{{ method.name }}</h5>
                        </div>
                        <div *ngIf="!isMethodSupported(method)" class="item-header-actions">
                          <h5 class="unsupported-label">Upgrade Required</h5>
                          <mat-icon  class="lock-icon" matTooltip="Requires plan upgrade" svgIcon="lock"></mat-icon>
                        </div>
                      </div>
                      <ng-container *ngIf="getParsedDescriptionForMethod(method) as parsedDesc; else plainDesc">
                        <ng-container *ngIf="parsedDesc && (parsedDesc.header || parsedDesc.when || parsedDesc.why); else simpleParsed">
                          <div *ngIf="parsedDesc.header" class="item-description-header">{{ parsedDesc.header }}</div>
                          <div class="item-description-lists border-top" *ngIf="parsedDesc.when?.length || parsedDesc.why?.length">
                            <div class="description-list-section" *ngIf="parsedDesc.when?.length">
                              <div class="list-label">When:</div>
                              <ul class="item-description-list">
                                <li *ngFor="let w of parsedDesc.when">{{ w }}</li>
                              </ul>
                            </div>
                            <div class="description-list-section" *ngIf="parsedDesc.why?.length">
                              <div class="list-label">Why:</div>
                              <ul class="item-description-list">
                                <li *ngFor="let r of parsedDesc.why">{{ r }}</li>
                              </ul>
                            </div>
                          </div>
                        </ng-container>
                        <ng-template #simpleParsed>
                          <p class="item-description">{{ parsedDesc }}</p>
                        </ng-template>
                      </ng-container>
                      <ng-template #plainDesc>
                        <p class="item-description" *ngIf="getMethodDescription(method)">{{ getMethodDescription(method) }}</p>
                      </ng-template>
                      <!-- Complexity Badge -->
                      <div *ngIf="isMethodSupported(method)"  class="complexity-badge" [class]="getComplexityClass(method.complexity)">
                        {{ getComplexityLabel(method.complexity) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ng-container>
      
      <!-- Upgrade Message -->
      <ng-container *ngIf="editorMode === 'upgrade' && !loadingMethods">
        <div class="upgrade-message">
          <div class="upgrade-content">
            <mat-icon class="upgrade-icon" svgIcon="lock"></mat-icon>
            <h2>Upgrade Required</h2>
            <p class="upgrade-description">
              <strong>{{ tempSelectedMethod?.name }}</strong> is not available on your current plan.
            </p>
            <p class="upgrade-benefits">
              Upgrade your plan to access advanced anomaly detection methods and unlock more powerful analysis capabilities.
            </p>
            <div class="upgrade-actions">
              <button mat-raised-button color="primary" class="upgrade-button">
                <mat-icon svgIcon="upgrade"></mat-icon>
                Upgrade Plan
              </button>
              <button mat-stroked-button (click)="onBackToMethodSelection()">
                <mat-icon svgIcon="arrow_back"></mat-icon>
                Back to Methods
              </button>
            </div>
          </div>
        </div>
      </ng-container>
      
      <!-- Method Configuration -->
      <ng-container *ngIf="editorMode === 'method-config' && !loadingMethods && !loading">
        <div class="method-configuration" style="display: flex; flex-direction: column; height: 100%;">
          
          <div class="config-section" style="flex: 1; overflow-y: auto;">
            <form [formGroup]="parameterForm" (ngSubmit)="onSubmit()">
              
              <!-- Required Parameters -->
              <div class="parameters-section" *ngIf="requiredParameters.length > 0">
                
                <div *ngFor="let param of requiredParameters" class="parameter-field">
                  <!-- Column Array Multi-Select (Special Rendering) -->
                  <div *ngIf="param.type === 'column-array'" class="target-columns-field">
                    <label class="target-columns-label">
                      {{ param.label }}
                      <span *ngIf="param.required" class="required-indicator">*</span>
                    </label>
                    <p class="field-description" *ngIf="param.description">{{ param.description }}</p>
                    
                    <div class="columns-selection-container">
                      <div class="selection-actions">
                        <button type="button" mat-button (click)="selectAllColumns(param.name)" class="select-all-btn">
                          <mat-icon svgIcon="check_box"></mat-icon>
                          Select All
                        </button>
                        <button type="button" mat-button (click)="deselectAllColumns(param.name)" class="deselect-all-btn">
                          <mat-icon svgIcon="check_box_outline_blank"></mat-icon>
                          Deselect All
                        </button>
                      </div>
                      
                      <div class="task-columns-selector compact" *ngIf="availableColumns.length > 0">
                        <div *ngFor="let column of validColumns" class="column-chip-item">
                          <button 
                            type="button"
                            class="column-chip"
                            [class.included]="isColumnSelected(column.name, param.name)"
                            [class.excluded]="!isColumnSelected(column.name, param.name)"
                            (click)="toggleColumnSelection(column.name, param.name)">
                            <span class="column-name">{{ column.name }}</span>
                            <span class="column-type-badge" [class]="'type-' + column.type.toLowerCase()">{{ getFriendlyDataTypeName(column.type) }}</span>
                          </button>
                        </div>
                        
                        <!-- Invalid columns indicator -->
                        <div *ngIf="invalidColumnCount > 0" class="column-chip-item">
                          <div class="column-chip invalid-columns-chip">
                            <img src="assets/icons/info-circle-grey.svg" alt="Info" class="info-icon">
                            <span class="invalid-count-text">{{ invalidColumnCount }} invalid column{{ invalidColumnCount > 1 ? 's' : '' }} hidden</span>
                          </div>
                        </div>
                      </div>
                      
                      <div class="no-columns-message" *ngIf="availableColumns.length === 0">
                        <mat-icon svgIcon="info"></mat-icon>
                        <p>No columns available. Please ensure your data source has been loaded.</p>
                      </div>
                      
                      <mat-error *ngIf="parameterForm.get(param.name)?.touched && parameterForm.get(param.name)?.hasError('required')">
                        Please select at least one column
                      </mat-error>
                    </div>
                  </div>
                  
                  <!-- Single Column Selector -->
                  <div *ngIf="param.type === 'column'">
                    <mat-form-field appearance="outline" class="w-full">
                      <mat-label>{{ param.label }}</mat-label>
                      <mat-select [formControlName]="param.name" [required]="param.required">
                        <mat-option *ngFor="let column of availableColumns" [value]="column.name">
                          {{ column.name }} ({{ column.type }})
                        </mat-option>
                      </mat-select>
                      <mat-error>This field is required</mat-error>
                    </mat-form-field>
                    <p *ngIf="param.description" class="parameter-description">{{ param.description }}</p>
                  </div>
                  
                  <!-- Multi-Select Dropdown (Array with Enum) -->
                  <div *ngIf="param.type === 'multiselect'">
                    <mat-form-field appearance="outline" class="w-full">
                      <mat-label>{{ param.label }}</mat-label>
                      <mat-select [formControlName]="param.name" multiple [required]="param.required">
                        <mat-option *ngFor="let option of param.enum" [value]="option">
                          {{ option }}
                        </mat-option>
                      </mat-select>
                      <mat-error>This field is required</mat-error>
                    </mat-form-field>
                    <p *ngIf="param.description" class="parameter-description">{{ param.description }}</p>
                  </div>
                  
                  <!-- Select Input -->
                  <div *ngIf="param.type === 'select'">
                    <mat-form-field appearance="outline" class="w-full">
                      <mat-label>{{ param.label }}</mat-label>
                      <mat-select [formControlName]="param.name" required>
                        <mat-option *ngFor="let option of param.enum" [value]="option">
                          {{ option }}
                        </mat-option>
                      </mat-select>
                      <mat-error>This field is required</mat-error>
                    </mat-form-field>
                    <p *ngIf="param.description" class="parameter-description">{{ param.description }}</p>
                  </div>

                  <!-- Text/Number Input -->
                  <div *ngIf="param.type === 'text' || param.type === 'number'">
                    <mat-form-field appearance="outline" class="w-full">
                      <mat-label>{{ param.label }}</mat-label>
                      <input matInput 
                             [type]="param.type === 'number' ? 'number' : 'text'"
                             [formControlName]="param.name"
                             [min]="param.minimum ?? null"
                             [max]="param.maximum ?? null"
                             required>
                      <mat-error>This field is required</mat-error>
                    </mat-form-field>
                    <p *ngIf="param.description" class="parameter-description">{{ param.description }}</p>
                  </div>

                  <!-- Checkbox Input -->
                  <div *ngIf="param.type === 'checkbox'" class="checkbox-field">
                    <mat-checkbox [formControlName]="param.name" [required]="param.required">
                      {{ param.label }}
                      <span *ngIf="param.required" class="required-indicator">*</span>
                    </mat-checkbox>
                    <p class="checkbox-hint" *ngIf="param.description">{{ param.description }}</p>
                  </div>
                </div>
              </div>

              <!-- Advanced Parameters in Collapsible Panel -->
              <mat-expansion-panel *ngIf="advancedParameters.length > 0" class="help-expansion-panel">
                <mat-expansion-panel-header>
                  <mat-panel-title>
                    <mat-icon class="panel-icon">settings</mat-icon>
                    Advanced Settings
                  </mat-panel-title>
                </mat-expansion-panel-header>
                
                <div *ngFor="let param of advancedParameters" class="parameter-field">
                  <!-- Column Array Multi-Select (Special Rendering) -->
                  <div *ngIf="param.type === 'column-array'" class="target-columns-field">
                    <label class="target-columns-label">{{ param.label }}</label>
                    <p class="field-description" *ngIf="param.description">{{ param.description }}</p>
                    
                    <div class="columns-selection-container">
                      <div class="selection-actions">
                        <button type="button" mat-button (click)="selectAllColumns(param.name)" class="select-all-btn">
                          <mat-icon svgIcon="check_box"></mat-icon>
                          Select All
                        </button>
                        <button type="button" mat-button (click)="deselectAllColumns(param.name)" class="deselect-all-btn">
                          <mat-icon svgIcon="check_box_outline_blank"></mat-icon>
                          Deselect All
                        </button>
                      </div>
                      
                      <div class="task-columns-selector compact" *ngIf="availableColumns.length > 0">
                        <div *ngFor="let column of validColumns" class="column-chip-item">
                          <button 
                            type="button"
                            class="column-chip"
                            [class.included]="isColumnSelected(column.name, param.name)"
                            [class.excluded]="!isColumnSelected(column.name, param.name)"
                            (click)="toggleColumnSelection(column.name, param.name)">
                            <mat-icon class="status-icon" [svgIcon]="isColumnSelected(column.name, param.name) ? 'check_circle' : 'cancel'"></mat-icon>
                            <span class="column-name">{{ column.name }}</span>
                            <span class="column-type-badge" [class]="'type-' + column.type.toLowerCase()">{{ getFriendlyDataTypeName(column.type) }}</span>
                          </button>
                        </div>
                        
                        <!-- Invalid columns indicator -->
                        <div *ngIf="invalidColumnCount > 0" class="column-chip-item">
                          <div class="column-chip invalid-columns-chip">
                            <img src="assets/icons/info-circle-grey.svg" alt="Info" class="info-icon">
                            <span class="invalid-count-text">{{ invalidColumnCount }} invalid column{{ invalidColumnCount > 1 ? 's' : '' }} hidden</span>
                          </div>
                        </div>
                      </div>
                      
                      <div class="no-columns-message" *ngIf="availableColumns.length === 0">
                        <mat-icon svgIcon="info"></mat-icon>
                        <p>No columns available. Please ensure your data source has been loaded.</p>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Single Column Selector -->
                  <div *ngIf="param.type === 'column'">
                    <mat-form-field appearance="outline" class="w-full">
                      <mat-label>{{ param.label }}</mat-label>
                      <mat-select [formControlName]="param.name">
                        <mat-option *ngFor="let column of availableColumns" [value]="column.name">
                          {{ column.name }} ({{ column.type }})
                        </mat-option>
                      </mat-select>
                      <mat-hint *ngIf="param.description">{{ param.description }}</mat-hint>
                      <mat-hint *ngIf="param.default && !param.description">Default: {{ param.default }}</mat-hint>
                    </mat-form-field>
                  </div>
                  
                  <!-- Multi-Select Dropdown (Array with Enum) -->
                  <div *ngIf="param.type === 'multiselect'">
                    <mat-form-field *ngIf="param.type === 'multiselect'" appearance="outline" class="w-full">
                      <mat-label>{{ param.label }}</mat-label>
                      <mat-select [formControlName]="param.name" multiple>
                        <mat-option *ngFor="let option of param.enum" [value]="option">
                          {{ option }}
                        </mat-option>
                      </mat-select>
                      <mat-hint *ngIf="param.description">{{ param.description }}</mat-hint>
                    </mat-form-field>
                    <p class="checkbox-hint" *ngIf="param.description">{{ param.description }}</p>
                  </div>
                  
                  <!-- Select Input -->
                  <div *ngIf="param.type === 'select'">
                    <mat-form-field appearance="outline" class="w-full">
                      <mat-label>{{ param.label }}</mat-label>
                      <mat-select [formControlName]="param.name">
                        <mat-option *ngFor="let option of param.enum" [value]="option">
                          {{ option }}
                        </mat-option>
                      </mat-select>
                      <mat-hint *ngIf="param.description">{{ param.description }}</mat-hint>
                      <mat-hint *ngIf="param.default && !param.description">Default: {{ param.default }}</mat-hint>
                    </mat-form-field>
                  
                    <p class="checkbox-hint" *ngIf="param.description">{{ param.description }}</p>
                  </div>

                  <!-- Number Input -->
                  <div *ngIf="param.type === 'number'">
                    <mat-form-field *ngIf="param.type === 'number'" appearance="outline" class="w-full">
                      <mat-label>{{ param.label }}</mat-label>
                      <input matInput 
                            type="number"
                            [formControlName]="param.name"
                            [min]="param.minimum ?? null"
                            [max]="param.maximum ?? null"
                            [placeholder]="'Default: ' + param.default">
                      <mat-hint *ngIf="param.description">{{ param.description }}</mat-hint>
                    </mat-form-field>
                    <p class="checkbox-hint" *ngIf="param.description">{{ param.description }}</p>
                  </div>

                  <!-- Checkbox Input -->
                  <div *ngIf="param.type === 'checkbox'" class="checkbox-field">
                    <mat-checkbox [formControlName]="param.name">
                      {{ param.label }}
                    </mat-checkbox>
                    <p class="checkbox-hint" *ngIf="param.description">{{ param.description }}</p>
                  </div>

                  <!-- Text Input -->
                  <div *ngIf="param.type === 'text'">
                    <mat-form-field *ngIf="param.type === 'text'" appearance="outline" class="w-full">
                      <mat-label>{{ param.label }}</mat-label>
                      <input matInput 
                            type="text"
                            [formControlName]="param.name"
                            [placeholder]="param.default ? 'Default: ' + param.default : ''">
                      <mat-hint *ngIf="param.description">{{ param.description }}</mat-hint>
                    </mat-form-field>
                    <p class="checkbox-hint" *ngIf="param.description">{{ param.description }}</p>
                  </div>
                </div>
              </mat-expansion-panel>

            </form>
          </div>
          
          <!-- Run Anomaly Detection Button - Pinned to Bottom -->
          <div style="position: sticky; bottom: 0; display: flex; justify-content: flex-end; padding: 24px; margin-top: auto; flex-shrink: 0;">
            <button mat-raised-button 
                    color="primary"
                    [disabled]="!canRunDetection() || loading"
                    (click)="onRunDetection()">
              <mat-icon *ngIf="!loading" svgIcon="play_circle_white"></mat-icon>
              <mat-spinner *ngIf="loading" diameter="20" style="display: inline-block; margin-right: 8px;"></mat-spinner>
              {{ loading ? 'Detecting Anomalies...' : 'Run Anomaly Detection' }}
            </button>
          </div>
        </div>
      </ng-container>

      <!-- Results View -->
      <ng-container *ngIf="editorMode === 'results'">
        <app-anomaly-detection-results 
          [results]="editorData" 
          [loading]="loading" 
          [runDate]="runDate"
          [dataSourceId]="dataSourceId"
          [uploadedFileData]="uploadedFileData"
          [availableColumns]="availableColumns"></app-anomaly-detection-results>
      </ng-container>
      
    </div>
  </div>
</div>
